import { IDynamicContent } from '@/modules/common/types';

export enum EProductTypes {
  FIXED_INCOME = 'fixed-income',
  GOVERNMENT_BOND = 'government-bond',
  FUNDS = 'funds',
  STRUCTURED_OPERATION = 'structured-operation',
  INITIAL_PUBLIC_OFFERING = 'initial-public-offering'
}

export interface IFixedIncomeReponse {
  title: string;
  filters: IFixedIncomeFilter[];
  items: IFixedIncomeProduct[];
  pagination: IPagination;
  marketStatus: IMarketStatus;
}

export interface IFixedIncomeFilter {
  label: string;
  value: string;
  isMultiple: boolean;
  selectedFilters: string | string[];
  options: { label: string; value: string }[];
}

export interface IFixedIncomeProduct {
  productId: string;
  risk: { label: string; value: string };
  name: string;
  category: string;
  minimumInvestment: string;
  maturityDate: string;
  liquidity: string;
  interestRate: string;
}

export interface IPagination {
  currentPage: number;
  previousPage: number;
  nextPage: number | null;
  pageSize: number;
  totalRecords: number;
}

export interface IProductsParams {
  pageIndex: number;
  pageSize: number;
  sortField?: string;
  sortDirection?: string;
  searchTerm?: string;
}

export interface ICustomOperationProduct {
  category: string;
  name: string;
  productId: string;
  minimumInvestment: string;
  maxGain: string;
  maxRisk: string;
  maturityDate: string;
  chips: {
    label: string;
    value: string;
  }[];
}

export interface ICustomOperationsResponse {
  title: string;
  items: ICustomOperationProduct[];
  pagination: IPagination;
  marketStatus: IMarketStatus;
}

export interface IGovernmentBondReponse {
  title: string;
  filters: IGovernmentBondFilter;
  items: IGovernmentBondProduct[];
  pagination: IPagination;
  marketStatus: IMarketStatus;
}

export interface IGovernmentBondProduct {
  productId: string;
  risk: { label: string; value: string };
  name: string;
  minimumInvestment: string;
  unityPrice: string;
  maturityDate: string;
  interestRate: string;
}

export interface IGovernmentBondFilter {
  selectedFilter: string;
  options: { label: string; value: string }[];
}

export interface IGovernmentBondParams {
  pageIndex: number;
  pageSize: number;
  segmentIds: string;
  sortField?: string;
  sortDirection?: string;
}

export interface IFundsReponse {
  title: string;
  filters: IFundsFilter[];
  items: IFundsProduct[];
  pagination: IPagination;
  marketStatus: IMarketStatus;
}

export interface IFundsProduct {
  productId: string;
  risk: { label: string; value: string };
  name: string;
  category: string;
  minimumInvestment: string;
  quotizationAtWithdraw: string;
  quotizationAtWithdrawType: string;
  liquidationAtWithdraw: string;
  liquidationAtWithdrawType: string;
  administrationTax: string;
  return: string;
  isProfessional: boolean;
  isQualified: boolean;
}

export interface IFundsFilter {
  label: string;
  value: string;
  isMultiple: boolean;
  selectedFilters: string | string[];
  options: { label: string; value: string }[];
}

export interface IProductDetails {
  isQualified: boolean;
  isProfessional: boolean;
  apiId: string;
  type: string;
  name: string;
  subCategory: string;
  details: { value: string; label: string }[];
  risk: { value: string; formatted: string };
  description: string;
  documents: IDocuments[];
}

export interface IDocuments {
  instrumentDetailId: number;
  description: string;
  title: string;
  url: string;
}

export interface IMarketStatus {
  isMarketOpen: boolean;
  icon: string;
  label: string;
  value: string;
}

export interface IProductHero {
  bar: string;
  head?: string;
  title: string;
  subtitle?: string;
  symbol: string;
  items: IProductHeroItem[];
  bottom: IProductHeroBottom;
  buttons: IProductHeroButton[];
  warnings: {
    icon: string;
    type: string;
    message: IDynamicContent[];
  };
}

export enum IProductHeroWarningType {
  INVESTOR_TYPE = 'investor_type',
  REGULATORY = 'regulatory_update'
}

export interface IProductHeroItem {
  label: string;
  value: string;
}

export interface IProductHeroBottom {
  label: string;
  value: string;
}

export interface IProductHeroButton {
  label: string;
  isActive: boolean;
  action: IProductHeroButtonAction;
}

export enum IProductHeroButtonAction {
  INVEST = 'INVEST',
  CHOICE = 'CHOICE',
  WARNING = 'WARNING',
  ADD = 'ADD',
  REMOVE = 'REMOVE'
}

export interface ICategoryProductDetails {
  hero: IProductHero;
  details: IProductDetailsV2;
}

export interface IProductDetailsV2 {
  sections: IProductDetailsSection[];
  costsDetails?: IProductCostsDetails;
  footer: IProductDetailFooter[];
  footerActions: IProductDetailFooterAction[];
}

export interface IProductDetailsSection {
  title: string;
  items: IProductsDetailItem[];
  message?: string;
  description?: string[];
}

export interface IProductsDetailItem {
  label: string;
  value: string;
  link?: string;
  description?: string;
}

export interface IProductDetailFooter {
  icon: string;
  label: string;
}

export interface IProductDetailFooterAction {
  icon: string;
  label: string;
  action: string;
}

export interface IProductCostsDetails {
  details: string;
  icon: string;
  title: string;
}

export interface IRisk {
  value: string;
  label: string;
}

export interface IInitialPublicOffering {
  category: string;
  name: string;
  productId: string;
  minimumInvestment: string;
  unitPrice: string;
  reserveDate: string;
  maturityDate: string;
  risk: IRisk[];
}

export interface IInitialPublicOfferingsResponse {
  title: string;
  items: IInitialPublicOffering[];
  pagination: IPagination;
}

export interface IProductPerformanceResponse {
  periods: IProductPerformancePeriod[];
  chartRange: string;
  chart: IProductPerformanceChart;
  updatedAt: string;
}

export interface IProductPerformancePeriod {
  percentageValue: { value: number; formatted: string };
  benchmarkReturn: { value: number; formatted: string };
  benchmarkComparison: { value: number; formatted: string };
}

export interface IProductPerformanceChart {
  axisX: IProductPerformanceAxisX[];
  axisY: IProductPerformanceAxisY[];
}

export interface IProductPerformanceAxisX {
  description: string;
  range: IProductPerformanceRange;
  values: IProductPerformanceValue[];
}

export interface IProductPerformanceRange {
  start: IProductPerformanceDate;
  end: IProductPerformanceDate;
}

export interface IProductPerformanceDate {
  value: string;
  formatted: string;
}

export interface IProductPerformanceValue {
  value: string;
  formatted: string;
}

export interface IProductPerformanceAxisY {
  name: string;
  type: string;
  values: IProductPerformanceChartValue[];
}

export interface IProductPerformanceChartValue {
  value: number;
  formatted: string;
}

export interface IProductCategoriesFiltersResponse {
  benchmarks: IProductBenchmark[];
  periods: IProductPeriod[];
}

export interface IProductBenchmark {
  label: string;
  value: string;
  active: boolean;
}

export interface IProductPeriod {
  label: string;
  value: string;
  active: boolean;
}

interface IInfoContent {
  type: string;
  text?: string;
  style?: string;
  link?: string;
  title?: string;
}

interface IInfoSection {
  label: string;
  content: IInfoContent[];
}

interface ICustomerServiceSection {
  title: string;
  phone: string;
  email: string;
}

interface ICustomerServices {
  title: string;
  sections: ICustomerServiceSection[];
}

export interface IProductAdditionalInfo {
  infoSections: IInfoSection[];
  customerServices: ICustomerServices;
}
